import HeartRobot from 'assets/robots/robot-heart.svg'
import { DEFAULT_RECOMMENDATIONS_ENDPOINT } from 'backend/lib/adapters/startPageAdapter'
import { EdithCardLayout } from 'backend/services/types/edithService.types'
import ContinuePlaying from 'components/ContinuePlaying/ContinuePlaying'
import IntroductionBanner from 'components/IntroductionBanner/IntroductionBanner'
import ProductListButtonContainer from 'components/ProductList/Buttons/ProductListButtonContainer'
import ProductListRemoveButton from 'components/ProductList/Buttons/ProductListRemoveButton'
import ProductListToggleButton from 'components/ProductList/Buttons/ProductListToggleButton'
import Header from 'components/ProductList/Header/Header'
import ProductList from 'components/ProductList/ProductList'
import ProductListCards from 'components/ProductList/ProductListCards/ProductListCards'
import Skeleton from 'components/ProductList/Skeleton/Skeleton'
import Recommendations from 'components/Recommendations/Recommendations'
import { ProductListLayout, SearchResponseType } from 'frontend/enums'
import Api from 'frontend/lib/Api'
import { Program } from 'frontend/types'
import { byIdInCustomOrder } from 'frontend/utils/helpers'
import { useStorage } from 'frontend/utils/hooks/useStorage'
import { useEffect, useState } from 'react'
import styles from './MyPage.module.css'

export const LOCAL_STORAGE_KEY = 'MY_SAVED_PRODUCTS'

export const useSaveToList = () => {
  const { storageValue: value, setStorageValue: setValue } =
    useStorage(LOCAL_STORAGE_KEY)

  const ids: number[] = value ? JSON.parse(value) : []

  const saveToList = (id: number) => {
    const nextIds = [id, ...ids]

    setValue(JSON.stringify(nextIds))
  }

  const removeFromList = (id: number) => {
    const nextIds = ids.filter((num: number) => num !== id)

    setValue(JSON.stringify(nextIds))
  }

  const isPresent = (id: number) => ids.some(num => num === id)

  const toggleIdInList = (id: number) =>
    isPresent(id) ? removeFromList(id) : saveToList(id)

  const setList = (nextIds: number[]) => setValue(JSON.stringify(nextIds))

  return { saveToList, removeFromList, toggleIdInList, isPresent, setList, ids }
}

const MyPage = () => {
  const [products, setProducts] = useState<Program[]>([])
  const [isEditing, setIsEditing] = useState(false)
  const [removeCardDraft, setRemoveCardDraft] = useState<number[]>([])
  const { setList, ids } = useSaveToList()
  const [noResultsReceived, setNoResultsReceived] = useState(false)
  const [layout, setLayout] = useState(ProductListLayout.SHOW_LESS)

  useEffect(() => {
    if (ids.length === 0) return

    const fetchData = async () => {
      const stringifiedProgramIds = ids.map(id => id.toString())
      const productsResponse = await Api.getSearchResults({
        id: stringifiedProgramIds,
        response_type: SearchResponseType.LIMITED,
      })

      if (productsResponse) {
        const sortedResults = productsResponse.results.sort(
          byIdInCustomOrder(stringifiedProgramIds),
        )
        setProducts(sortedResults as Program[])
      } else {
        setNoResultsReceived(true)
      }
    }
    fetchData()
  }, [ids.length])

  const handleStartEditing = () => setIsEditing(true)

  const handleEndEditing = () => {
    const nextProducts = products.filter(p => !removeCardDraft.includes(p.id))

    setProducts(nextProducts)
    setList(nextProducts.map(p => p.id))
    setIsEditing(false)
    setRemoveCardDraft([])
  }

  const Fallback = () => (
    <div className={styles.noProgramsSaved}>
      <HeartRobot className={styles.robot} />
      <p className={styles.noProgramsText}>
        Du har ännu inte sparat några program
      </p>
    </div>
  )

  const ProductListContent = () => (
    <ProductListCards
      headingLevel={3}
      cardLayout={EdithCardLayout.DEFAULT}
      isEditing={isEditing}
      showAllCards={layout === ProductListLayout.SHOW_MORE}
      products={
        layout === ProductListLayout.SHOW_LESS ? products.slice(0, 5) : products
      }
      removeCardDraft={removeCardDraft}
      setRemoveCardDraft={setRemoveCardDraft}
      blockTracking={{
        blockIndex: 1,
        blockType: 'my_saved_programs',
        blockTitle: 'Mina sparade program',
      }}
    />
  )

  const isFallBack = ids.length === 0 || noResultsReceived

  const MyPageList = () => {
    if (isFallBack) return <Fallback />

    if (products.length > 0) return <ProductListContent />

    return <Skeleton />
  }

  return (
    <>
      <IntroductionBanner title="Min sida">
        <p>
          Spara dina favoritprogram eller program du vill streama senare på Min
          sida.
        </p>
        <p>
          Dina sparade program under Min sida kan bara nås från den dator,
          surfplatta eller mobil där du sparade programmet.
        </p>
      </IntroductionBanner>
      <div className={styles.wrapper}>
        <ul>
          <li className={styles.blocks}>
            <ProductList>
              <Header title={'Mina sparade program'}>
                {!isFallBack && (
                  <ProductListButtonContainer>
                    <ProductListRemoveButton
                      isEditing={isEditing}
                      onClick={
                        isEditing ? handleEndEditing : handleStartEditing
                      }
                    />
                    <ProductListToggleButton
                      numProducts={products.length}
                      setLayout={setLayout}
                      layout={layout}
                    />
                  </ProductListButtonContainer>
                )}
              </Header>
              <MyPageList />
            </ProductList>
          </li>
          <li className={styles.blocks}>
            <ContinuePlaying
              blockTracking={{
                blockIndex: 2,
                blockType: 'continue_playing',
                blockTitle: 'Fortsätt spela',
              }}
            />
          </li>
          <li className={styles.blocks}>
            <Recommendations
              title="Tips för dig"
              endpoint={DEFAULT_RECOMMENDATIONS_ENDPOINT}
              blockTracking={{
                blockIndex: 3,
                blockType: 'recommendations',
                blockTitle: 'Tips för dig',
              }}
            />
          </li>
        </ul>
      </div>
    </>
  )
}

export default MyPage
